/**
 * Comparación de mercado: normalizador puro de `GET /getmarketcomparison` y textos de la
 * tarjeta «Precio vs. mercado». Ver docs/maps/market-comparison.md.
 *
 * Regla: nunca se muestra la fuente de los datos. El normalizador arma objetos nuevos con
 * una lista cerrada de campos, así que `debug` (staff) o cualquier campo extra de la API
 * nunca llega a la UI.
 */

const VERDICTS = ['alto', 'en_linea', 'bajo'];
const CONFIDENCES = ['baja', 'media', 'alta'];
const BASES = ['similares', 'general'];
const OPERATIONS = ['venta', 'alquiler'];
const MATCHES = ['similar', 'otros_dormitorios', 'otro_tamano', 'sin_referencia'];
const SCOPES = ['radio', 'radio_ampliado', 'radio_o_barrio'];

export const MARKET_VERDICT_LABELS = Object.freeze({
  alto: 'Alto',
  en_linea: 'En línea',
  bajo: 'Bajo',
});
export const MARKET_CONFIDENCE_LABELS = Object.freeze({ baja: 'baja', media: 'media', alta: 'alta' });
export const MARKET_MATCH_LABELS = Object.freeze({
  similar: 'Parecida',
  otros_dormitorios: 'Otra cantidad de dormitorios',
  otro_tamano: 'Otro tamaño',
  sin_referencia: '',
});
export const MARKET_OPERATION_LABELS = Object.freeze({ venta: 'Venta', alquiler: 'Alquiler' });

/** Mínimo de comparables para que haya mediana (la API devuelve `sin_datos` debajo). */
export const MARKET_MIN_COMPARABLES = 5;

const MONTHS_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

const num = (v) => {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const int = (v) => {
  const n = num(v);
  return n === null ? null : Math.trunc(n);
};
const str = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
const oneOf = (v, allowed) => (typeof v === 'string' && allowed.includes(v) ? v : null);
const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : null);
const arr = (v) => (Array.isArray(v) ? v : []);

/**
 * Período de `price_trend` → texto corto.
 * `"2026-T3"` → `"T3 2026"`; `"2026-09"` → `"sep 2026"`. Otro formato: tal cual.
 */
export function formatMarketPeriod(period) {
  const p = str(period);
  if (!p) return '';
  const q = /^(\d{4})-T([1-4])$/i.exec(p);
  if (q) return `T${q[2]} ${q[1]}`;
  const m = /^(\d{4})-(\d{2})$/.exec(p);
  if (m) {
    const idx = Number(m[2]) - 1;
    if (idx >= 0 && idx < 12) return `${MONTHS_SHORT[idx]} ${m[1]}`;
  }
  return p;
}

/** Mes de un comparable (`"2026-10"`) → `"oct 2026"`. */
export const formatMarketMonth = (value) => formatMarketPeriod(value);

function readStats(raw) {
  const s = obj(raw);
  if (!s) return null;
  const median = num(s.median);
  if (median === null) return null;
  return {
    n: int(s.n) ?? 0,
    median,
    p25: num(s.p25),
    p75: num(s.p75),
    dateFrom: str(s.date_from),
    dateTo: str(s.date_to),
    // Alquiler (API f30da51): esperable para la superficie de la unidad y
    // mediana de los totales reales. En venta vienen null.
    expectedPriceTotal: readTotals(s.expected_price_total, true),
    priceTotal: readTotals(s.price_total, false),
  };
}

/** `{median, p25, p75}` (+ `surface_m2` o `n`) de totales por mes; null sin mediana. */
function readTotals(raw, withSurface) {
  const t = obj(raw);
  if (!t) return null;
  const median = num(t.median);
  if (median === null) return null;
  const out = { median, p25: num(t.p25), p75: num(t.p75) };
  if (withSurface) out.surfaceM2 = num(t.surface_m2);
  else out.n = int(t.n) ?? 0;
  return out;
}

function readSearch(raw, unit) {
  const s = obj(raw);
  if (!s) return null;
  const scope = oneOf(s.scope, SCOPES) || str(s.scope);
  const radiusM = int(s.radius_m);
  const months = int(s.months);
  let scopeLabel = null;
  if (scope === 'radio_o_barrio') {
    scopeLabel = unit?.barrio ? `Zona ampliada al barrio ${unit.barrio}` : 'Zona ampliada al barrio';
  } else if (scope === 'radio_ampliado') {
    scopeLabel = 'Radio ampliado';
  }
  return {
    scope,
    scopeLabel,
    radiusM,
    months,
    widened: Boolean(s.widened) || scope === 'radio_ampliado' || scope === 'radio_o_barrio',
  };
}

function readByBedrooms(raw) {
  if (!Array.isArray(raw)) return null;
  const rows = raw.map((r) => {
    const row = obj(r);
    if (!row) return null;
    const median = num(row.median);
    const bedrooms = int(row.bedrooms);
    if (median === null || bedrooms === null) return null;
    return {
      bedrooms,
      label: str(row.label) || (bedrooms === 0 ? 'Monoambiente' : String(bedrooms)),
      n: int(row.n) ?? 0,
      median,
      p25: num(row.p25),
      p75: num(row.p75),
      // Alquiler: `{n, median, p25, p75}` de los totales reales del grupo (null si n < 5 o
      // respuesta vieja).
      priceTotal: readTotals(row.price_total, false),
    };
  }).filter(Boolean);
  return rows.length ? rows : null;
}

function readTrend(raw) {
  const t = obj(raw);
  if (!t) return null;
  const points = arr(t.points).map((p) => {
    const point = obj(p);
    const period = str(point?.period);
    if (!period) return null;
    return {
      period,
      // La API manda el texto listo («3.º trim. 2026», «sep 2026»), también en los huecos;
      // el formateo del Front queda de respaldo.
      label: str(point.label) || formatMarketPeriod(period),
      n: int(point.n) ?? 0,
      // `median: null` = período sin datos suficientes: no se dibuja ni se une la línea.
      median: num(point.median),
      // Alquiler: esperable para la superficie de la unidad en ese período (solo se guarda).
      expectedPriceTotal: num(point.expected_price_total),
      gapBefore: Boolean(point.gap_before),
    };
  }).filter(Boolean);
  if (points.filter((p) => p.median !== null).length < 2) return null;
  const changePct = num(t.change_pct);
  const changeFrom = changePct === null ? null : str(t.change_from);
  const changeTo = changePct === null ? null : str(t.change_to);
  const hasGaps = Boolean(t.has_gaps) || points.some((p) => p.median === null);
  return {
    granularity: t.granularity === 'mes' ? 'mes' : 'trimestre',
    nominal: Boolean(t.nominal),
    points,
    changePct,
    changeFrom,
    changeTo,
    changeFromLabel: changeFrom ? (str(t.change_from_label) || formatMarketPeriod(changeFrom)) : null,
    changeToLabel: changeTo ? (str(t.change_to_label) || formatMarketPeriod(changeTo)) : null,
    hasGaps,
    note: hasGaps ? (str(t.note) || 'hay períodos sin datos') : null,
  };
}

function readPin(raw, approx) {
  const p = obj(raw);
  if (!p) return null;
  const lat = num(p.lat);
  const lng = num(p.lng);
  if (lat === null || lng === null || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng, approx: Boolean(approx) };
}

function readPhotos(raw) {
  return arr(raw)
    .filter((u) => typeof u === 'string' && /^https:\/\//i.test(u.trim()))
    .map((u) => u.trim())
    .slice(0, 3);
}

function readComparable(raw, index) {
  const c = obj(raw);
  if (!c) return null;
  const match = oneOf(c.match, MATCHES);
  const pin = readPin(c.pin, c.pin_approx);
  // Solo campos conocidos: nunca fuente, link, id externo, título, contacto ni inmobiliaria.
  return {
    // `key`: id estable y opaco de la API (pines, clústeres y key de React). Si falta
    // (raro), el índice dentro del bloque.
    key: str(c.key) || `c${index}`,
    index,
    terrioType: str(c.terrio_type),
    operation: oneOf(c.operation, OPERATIONS),
    surfaceM2: num(c.surface_m2),
    surfaceBasis: str(c.surface_basis),
    pricePerM2: num(c.price_per_m2),
    priceTotal: num(c.price_total),
    priceTotalPeriod: c.price_total_period === 'mes' ? 'mes' : null,
    currency: str(c.currency),
    date: str(c.date),
    dateLabel: formatMarketMonth(c.date),
    barrio: str(c.barrio),
    // Sin dato la API manda 0 (también mismo edificio).
    distanceM: int(c.distance_m) ?? 0,
    pin,
    pinApprox: Boolean(pin?.approx),
    rooms: int(c.rooms),
    bedrooms: int(c.bedrooms),
    bathrooms: int(c.bathrooms),
    parking: int(c.parking),
    photos: readPhotos(c.photos),
    match,
    matchLabel: match ? (str(c.match_label) ?? MARKET_MATCH_LABELS[match]) : null,
    // Tamaño muy cercano al de la unidad (la API ya los ordena primero). Solo `true` cuenta.
    verySimilar: c.very_similar === true,
  };
}

/** Rango que define «parecida» (`similar_criteria`); null si no aplica. */
function readSimilarCriteria(raw) {
  const c = obj(raw);
  if (!c) return null;
  const out = {
    surfaceMinM2: num(c.surface_min_m2),
    surfaceMaxM2: num(c.surface_max_m2),
    bedroomsMin: int(c.bedrooms_min),
    bedroomsMax: int(c.bedrooms_max),
  };
  return Object.values(out).some((v) => v !== null) ? out : null;
}

function readSide(diffPct, verdict, confidence) {
  const v = oneOf(verdict, VERDICTS);
  if (!v) return null;
  return { diffPct: num(diffPct), verdict: v, confidence: oneOf(confidence, CONFIDENCES) };
}

/** La API puede mandar la operación cruda («alquiler_temporario»): se muestra con su rótulo. */
const RAW_OPERATION_LABELS = { venta: 'Venta', alquiler: 'Alquiler', alquiler_temporario: 'Alquiler temporario' };
function readOperationLabel(raw, operation) {
  const label = str(raw);
  if (label && RAW_OPERATION_LABELS[label]) return RAW_OPERATION_LABELS[label];
  return label || (operation ? MARKET_OPERATION_LABELS[operation] : '');
}

function readBlock(raw, unit, nested = false) {
  const b = obj(raw);
  if (!b) return null;
  const operation = oneOf(b.operation, OPERATIONS);
  const reason = str(b.reason);
  // Sin veredicto (`reason`) la API manda null; si viniera algo igual, se descarta.
  const verdict = reason ? null : oneOf(b.verdict, VERDICTS);
  const market = readStats(b.market);
  return {
    operation,
    operationLabel: readOperationLabel(b.operation_label, operation),
    metric: str(b.metric),
    metricLabel: str(b.metric_label),
    currency: str(b.currency),
    unitValuePerM2: num(b.unit_value_per_m2),
    unitValueConverted: Boolean(b.unit_value_converted),
    // Alquiler (API f30da51): total por mes de la unidad (viene aunque la superficie sea
    // dudosa; null en `otra_moneda`) y período. Con `price_total_period: "mes"` (o el campo
    // `unit_price_total` en la respuesta) los totales son de la API y no se calculan en el Front.
    unitPriceTotal: num(b.unit_price_total),
    priceTotalPeriod: b.price_total_period === 'mes' ? 'mes' : null,
    totalsFromApi: b.price_total_period === 'mes' || b.unit_price_total !== undefined,
    market,
    marketSimilar: readStats(b.market_similar),
    nComparables: int(b.n_comparables) ?? (market ? market.n : null),
    nSimilar: int(b.n_similar),
    excludedOutliers: int(b.excluded_outliers) ?? 0,
    // Avisos casi duplicados (mismo inmueble) sacados del cálculo: una vez por bloque, entero
    // ≥ 0 (0 en el bloque vacío). Solo se guarda. `null` = respuesta vieja sin el campo.
    duplicatesRemoved: (() => { const d = int(b.duplicates_removed); return d !== null && d >= 0 ? d : null; })(),
    verdict,
    verdictLabel: verdict ? (str(b.verdict_label) || MARKET_VERDICT_LABELS[verdict]) : null,
    diffPct: verdict ? num(b.diff_pct) : null,
    confidence: verdict ? oneOf(b.confidence, CONFIDENCES) : null,
    basis: market ? oneOf(b.verdict_basis, BASES) : null,
    basisLabel: str(b.verdict_basis_label),
    basisReason: str(b.verdict_basis_reason),
    basisReasonLabel: str(b.verdict_basis_reason_label),
    general: reason ? null : readSide(b.diff_pct_general, b.verdict_general, b.confidence_general),
    similar: reason ? null : readSide(b.diff_pct_similar, b.verdict_similar, b.confidence_similar),
    reason,
    reasonLabel: str(b.reason_label),
    similarCriteria: readSimilarCriteria(b.similar_criteria),
    search: readSearch(b.search, unit),
    byBedrooms: readByBedrooms(b.by_bedrooms),
    priceTrend: readTrend(b.price_trend),
    // Muestra baja (API 830ad6c): 1 a 4 comparables válidos tras ampliar la búsqueda; sigue
    // `sin_datos` (sin mediana ni veredicto) pero vienen en `comparables[]` para mostrar.
    lowSample: b.low_sample === true && reason === 'sin_datos' && arr(b.comparables).length > 0,
    // Hay comparables cuando hay mediana o con muestra baja.
    comparables: market || (b.low_sample === true && reason === 'sin_datos')
      ? arr(b.comparables).map(readComparable).filter(Boolean).slice(0, 20) : [],
    // Alquiler: línea aparte con los avisos publicados en USD (USD/m² por mes, sin tipo de
    // cambio). Misma forma que el bloque y sin `usd_line` propio. `withCurrencyLine` la
    // resuelve (no sale del normalizador con este nombre).
    usdLine: !nested && operation === 'alquiler' ? readBlock(b.usd_line, unit, true) : null,
  };
}

/** Línea de referencia (la otra moneda): nunca da veredicto, aunque la API mande algo. */
function asReference(line) {
  if (!line) return null;
  const { usdLine, ...rest } = line;
  return {
    ...rest,
    verdict: null,
    verdictLabel: null,
    diffPct: null,
    confidence: null,
    general: null,
    similar: null,
    otherLine: null,
  };
}

/**
 * Alquiler con dos monedas (decisión B, 30 oct): la línea vigente es la de la moneda de la
 * unidad. Unidad alquilada en USD (o bloque en pesos con `otra_moneda`) y `usd_line` que no
 * es `otra_moneda` → manda `usd_line` (USD/m² por mes, sin conversión) y la de pesos queda
 * como referencia en `otherLine`. Si no, al revés. Las monedas nunca se mezclan.
 */
function withCurrencyLine(block, unit) {
  const { usdLine, ...base } = block;
  if (!usdLine) return { ...base, otherLine: null };
  const unitInUsd = unit?.currency === 'USD' || base.reason === 'otra_moneda';
  if (unitInUsd && usdLine.reason !== 'otra_moneda') {
    const { usdLine: _ignored, ...usd } = usdLine;
    return { ...usd, otherLine: asReference(base) };
  }
  return { ...base, otherLine: asReference(usdLine) };
}

/**
 * Redondeo de un total esperable (mismo que la API): en pesos, a 100 debajo de 100.000 y a
 * 1.000 arriba; en USD (`usd_line`, API 9fabcfd), a 10.
 */
export function roundMarketMonthly(value, currency = null) {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  if (currency === 'USD') return Math.round(value / 10) * 10;
  const step = Math.abs(value) >= 100000 ? 1000 : 100;
  return Math.round(value / step) * step;
}

/**
 * Alquiler en total por mes (decisión 30 oct). La API (f30da51, «Alquiler: totales por mes»)
 * manda `unit_price_total`, `market(_similar).expected_price_total` (= /m² × m² de la unidad;
 * null sin superficie o con `superficie_dudosa`) y `by_bedrooms[].price_total` (totales
 * reales); si el bloque los trae (`totalsFromApi`) se usan tal cual, también los null.
 * Respuesta vieja: cálculo local con la superficie del veredicto (`unit.surface_m2`) y el
 * mismo redondeo. Sin superficie confiable no hay esperable (`areaM2: null`), pero sí el total
 * de la unidad si hay.
 *
 * @returns {{ areaM2: number|null, unitTotal: number|null, market: object|null,
 *   marketSimilar: object|null, byBedrooms: object[]|null, byBedroomsBasis: string|null }|null}
 */
export function rentMonthlyEquivalent(block, unit) {
  if (!block || block.operation !== 'alquiler') return null;
  const rows = Array.isArray(block.byBedrooms) ? block.byBedrooms : null;
  if (block.totalsFromApi) {
    const fromApi = (st) => (st?.expectedPriceTotal
      ? { n: st.n, median: st.expectedPriceTotal.median, p25: st.expectedPriceTotal.p25, p75: st.expectedPriceTotal.p75 }
      : null);
    const market = fromApi(block.market);
    const marketSimilar = fromApi(block.marketSimilar);
    const areaM2 = block.market?.expectedPriceTotal?.surfaceM2
      ?? block.marketSimilar?.expectedPriceTotal?.surfaceM2
      ?? ((market || marketSimilar) ? num(unit?.surfaceM2) : null);
    const out = {
      areaM2: market || marketSimilar ? areaM2 : null,
      unitTotal: block.unitPriceTotal,
      market,
      marketSimilar,
      byBedroomsBasis: rows ? 'reales' : null,
      byBedrooms: rows ? rows.map((r) => ({
        ...r,
        n: r.priceTotal?.n ?? r.n,
        median: r.priceTotal?.median ?? null,
        p25: r.priceTotal?.p25 ?? null,
        p75: r.priceTotal?.p75 ?? null,
      })) : null,
    };
    return out.unitTotal !== null || out.market || out.marketSimilar || out.byBedrooms ? out : null;
  }
  // Respuesta vieja: cálculo local.
  const dubious = unit?.surfacePlausible === false || block.reason === 'superficie_dudosa';
  const rawArea = num(unit?.surfaceM2);
  const area = !dubious && rawArea !== null && rawArea > 0 ? rawArea : null;
  const price = num(unit?.price);
  const sameCurrency = !unit?.currency || !block.currency || unit.currency === block.currency;
  let unitTotal = null;
  if (price !== null && price > 0 && sameCurrency && !block.unitValueConverted) unitTotal = price;
  else if (area !== null && block.unitValuePerM2 !== null && block.unitValuePerM2 !== undefined) {
    unitTotal = Math.round(block.unitValuePerM2 * area);
  }
  if (area === null) {
    return unitTotal !== null
      ? { areaM2: null, unitTotal, market: null, marketSimilar: null, byBedrooms: null, byBedroomsBasis: null }
      : null;
  }
  const times = (v) => (v === null || v === undefined ? null : roundMarketMonthly(v * area, block.currency));
  const scale = (st) => (st ? { n: st.n, median: times(st.median), p25: times(st.p25), p75: times(st.p75) } : null);
  return {
    areaM2: area,
    unitTotal,
    market: scale(block.market),
    marketSimilar: scale(block.marketSimilar),
    byBedroomsBasis: rows ? 'superficie' : null,
    byBedrooms: rows ? rows.map((r) => ({ ...r, median: times(r.median), p25: times(r.p25), p75: times(r.p75) })) : null,
  };
}

function withMonthly(block, unit) {
  const monthly = rentMonthlyEquivalent(block, unit);
  const otherLine = block.otherLine
    ? { ...block.otherLine, monthly: rentMonthlyEquivalent(block.otherLine, { ...unit, price: null }) }
    : null;
  return { ...block, monthly, otherLine };
}

function readUnit(raw) {
  const u = obj(raw) || {};
  const range = obj(u.surface_range);
  const attrs = obj(u.attributes) || {};
  return {
    itemKind: str(u.item_kind),
    terrioType: str(u.terrio_type),
    terrioTypeLabel: str(u.terrio_type_label),
    operation: str(u.operation),
    price: num(u.price),
    currency: str(u.currency),
    surfaceM2: num(u.surface_m2),
    surfaceBasis: str(u.surface_basis),
    surfacePlausible: u.surface_plausible !== false,
    surfaceRange: range ? { min: num(range.min), max: num(range.max) } : null,
    barrio: str(u.barrio),
    hasLocation: u.has_location !== false,
    attributes: { bedrooms: int(attrs.bedrooms), bathrooms: int(attrs.bathrooms) },
  };
}

/**
 * Normaliza la respuesta 200 de `/getmarketcomparison` (acepta el envelope o `data`).
 *
 * @returns {{ generatedOn: string|null, unit: object, blocks: object[], notes: string[] }|null}
 */
export function normalizeMarketComparison(payload) {
  const root = obj(payload);
  if (!root) return null;
  const data = obj(root.data) && (root.data.unit || root.data.comparisons) ? root.data : root;
  if (!obj(data) || (!data.unit && !data.comparisons)) return null;
  const unit = readUnit(data.unit);
  const blocks = arr(data.comparisons).map((b) => readBlock(b, unit)).filter(Boolean)
    .map((b) => withMonthly(withCurrencyLine(b, unit), unit));
  return {
    generatedOn: str(data.generated_on),
    unit,
    blocks,
    notes: arr(data.notes).map(str).filter(Boolean),
  };
}

// ---------------------------------------------------------------------------
// Formato

const nf = (digits = 0) => new Intl.NumberFormat('es-AR', { maximumFractionDigits: digits, minimumFractionDigits: 0 });

/**
 * `1900` → `"1.900"`. Debajo de 100 con un decimal (alquiler en USD/m² por mes: `11.5` →
 * `"11,5"`; lotes en alquiler).
 */
export function formatMarketNumber(value) {
  const n = num(value);
  if (n === null) return '';
  if (Math.abs(n) < 100) return nf(1).format(Math.round(n * 10) / 10);
  return nf(0).format(Math.round(n));
}

/** Rótulo de la línea de referencia: «Alquileres publicados en dólares» / «en pesos». */
export function marketReferenceTitle(line) {
  if (!line) return '';
  const what = line.operation === 'venta' ? 'Ventas' : 'Alquileres';
  if (line.currency === 'USD') return `${what} publicados en dólares`;
  if (line.currency === 'ARS') return `${what} publicados en pesos`;
  return line.operationLabel || what;
}

/** «Se descartaron 3 avisos repetidos» (solo con N > 0, del bloque vigente). */
export function describeMarketDuplicates(block) {
  const n = int(block?.duplicatesRemoved);
  if (!n || n <= 0) return '';
  return n === 1 ? 'Se descartó 1 aviso repetido' : `Se descartaron ${n} avisos repetidos`;
}

/** Valor por m²: venta `"USD 1.900/m²"`; alquiler `"ARS 9.800/m² por mes"`. */
export function formatMarketPerM2(value, currency, operation) {
  const n = formatMarketNumber(value);
  if (!n) return '';
  const cur = currency || (operation === 'alquiler' ? 'ARS' : 'USD');
  return `${cur} ${n}/m²${operation === 'alquiler' ? ' por mes' : ''}`;
}

/** Total por mes: `650000, 'ARS'` → «ARS 650.000/mes». */
export function formatMarketMonthly(value, currency) {
  const n = formatMarketNumber(value);
  if (!n) return '';
  return `${currency || 'ARS'} ${n}/mes`;
}

/** Rango por mes: «ARS 1.100.000–1.400.000/mes» (`withPeriod: false` sin «/mes»). */
export function formatMarketMonthlyRange(p25, p75, currency, { withPeriod = true } = {}) {
  const a = formatMarketNumber(p25);
  const b = formatMarketNumber(p75);
  if (!a || !b) return '';
  return `${currency || 'ARS'} ${a}–${b}${withPeriod ? '/mes' : ''}`;
}

/** Rótulo del esperable: «Esperable para 50 m²». */
export function marketMonthlyAreaLabel(monthly) {
  const m2 = formatMarketNumber(monthly?.areaM2);
  return m2 ? `Esperable para ${m2} m²` : 'Esperable';
}

/** Diferencia contra la mediana: `-2.4` → `"2,4 % por debajo"`. */
export function describeMarketDiff(diffPct) {
  const d = num(diffPct);
  if (d === null) return '';
  const abs = Math.abs(d);
  if (abs < 0.05) return 'Igual a la mediana';
  const txt = nf(1).format(Math.round(abs * 10) / 10);
  return `${txt} % ${d < 0 ? 'por debajo' : 'por encima'}`;
}

/** Margen mínimo (en % del ancho) entre cualquier marca de la barra y sus bordes. */
export const MARKET_BAR_EDGE_PCT = 8;

/**
 * Posición (0–100) de la mediana, P25–P75 y la unidad en la barra. La escala cubre siempre el
 * rango medio **y la unidad**, con aire a los dos lados (la mitad del tramo cubierto y como
 * mínimo el 10 % de la mediana): ninguna marca queda pegada a un borde, aunque la unidad caiga
 * justo afuera del rango (p. ej. 2.000 con P75 1.984).
 */
export function computeMarketBar({ p25, p75, median, value }) {
  const vals = [p25, p75, median, value].map(num).filter((v) => v !== null);
  if (vals.length < 2) return null;
  const lo = Math.min(...vals);
  const hi = Math.max(...vals);
  const ref = Math.abs(num(median) ?? (lo + hi) / 2) || 1;
  const span = Math.max(hi - lo, ref * 0.1);
  const pad = Math.max(span * 0.5, ref * 0.05);
  const min = (lo + hi) / 2 - span / 2 - pad;
  const total = span + pad * 2;
  const clamp = (x) => Math.min(100 - MARKET_BAR_EDGE_PCT, Math.max(MARKET_BAR_EDGE_PCT, x));
  const pos = (v) => (num(v) === null ? null : clamp(((num(v) - min) / total) * 100));
  const left = pos(p25);
  const right = pos(p75);
  return {
    iqrLeft: left,
    iqrWidth: left !== null && right !== null ? Math.max(0, right - left) : null,
    median: pos(median),
    value: pos(value),
  };
}

/**
 * Rango medio con la misma unidad en los dos extremos: «USD 1.417–1.984/m²»
 * (alquiler: «ARS 8.000–9.500/m² por mes»). '' si falta un extremo.
 */
export function formatMarketRange(p25, p75, currency, operation) {
  const a = formatMarketNumber(p25);
  const b = formatMarketNumber(p75);
  if (!a || !b) return '';
  const cur = currency || (operation === 'alquiler' ? 'ARS' : 'USD');
  return `${cur} ${a}–${b}/m²${operation === 'alquiler' ? ' por mes' : ''}`;
}

/*
 * Título y subtítulo de cada motivo sin veredicto (tarjeta: vacío mini; informe: aviso único).
 * Cortos: entran en la tarjeta de 132 px también en el Dock angosto (t1064u, opción A).
 */
const REASON_COPY = {
  superficie_dudosa: (unit) => {
    const m2 = formatMarketNumber(unit?.surfaceM2);
    return {
      title: 'La superficie no parece correcta',
      text: m2 ? `Cargaste ${m2} m². Revisala para comparar` : 'Revisala para comparar',
    };
  },
  precio_dudoso: () => ({ title: 'El precio no parece correcto', text: 'Revisá precio y superficie' }),
  sin_precio: () => ({ title: 'Falta el precio', text: 'Cargalo para comparar con el mercado' }),
  sin_superficie: () => ({ title: 'Falta la superficie', text: 'Cargala para comparar con el mercado' }),
  sin_tipo: () => ({ title: 'Falta el tipo de inmueble', text: 'Elegilo para comparar con el mercado' }),
  sin_operacion: () => ({ title: 'Falta la operación', text: 'Elegí venta o alquiler' }),
  sin_ubicacion: () => ({ title: 'Falta la ubicación', text: 'La unidad no está en el mapa' }),
  sin_fx: () => ({ title: 'Sin tipo de cambio', text: 'Probá más tarde' }),
  // Alquiler: esta línea es de la otra moneda que la de la unidad; solo referencia.
  otra_moneda: (unit, block) => (block?.currency === 'USD'
    ? { title: 'Se alquila en pesos', text: 'Los alquileres en dólares son solo referencia' }
    : { title: 'Se alquila en dólares', text: 'Los alquileres en pesos son solo referencia' }),
  operacion_no_soportada: (unit, block) => (unit?.operation === 'alquiler_temporario' || block?.operationLabel === 'Alquiler temporario'
    ? { title: 'Sin datos de alquiler temporario', text: 'Todavía no hay en esta zona' }
    : { title: 'Sin datos para esta operación', text: 'Todavía no hay en esta zona' }),
  otra_operacion: () => ({ title: 'Sin datos para esta operación', text: 'El mercado es de otra operación' }),
};
/** Motivo sin texto propio: `reason_label` de la API como subtítulo. */
const UNKNOWN_REASON_TITLE = 'No pudimos comparar esta unidad';
/** Falta un dato que se completa editando (ícono lápiz); el resto lleva «!». */
const MISSING_FIELD_REASONS = ['sin_precio', 'sin_superficie', 'sin_tipo', 'sin_operacion'];
/** Motivos que se arreglan editando la unidad (muestran «Editar unidad»). */
const EDITABLE_REASONS = ['superficie_dudosa', 'precio_dudoso', 'sin_precio', 'sin_superficie', 'sin_tipo', 'sin_operacion'];
const DUBIOUS_REASONS = ['superficie_dudosa', 'precio_dudoso'];

/** Bloque principal de la tarjeta: el de la operación de la unidad o el primero. */
export function pickMarketBlock(result) {
  const blocks = result?.blocks || [];
  if (!blocks.length) return null;
  const op = result.unit?.operation;
  return blocks.find((b) => b.operation && b.operation === op) || blocks[0];
}

/**
 * Rótulos de la barra. Venta: /m². Alquiler con superficie: total por mes para la superficie
 * de la unidad (la barra sigue en /m²: las proporciones son las mismas).
 */
function describeCardLabels(block, basisStats) {
  const m = block.monthly;
  const mStats = m?.areaM2 ? (basisStats === block.marketSimilar ? m.marketSimilar : m.market) : null;
  if (m && mStats && m.unitTotal !== null) {
    return {
      monthly: true,
      areaLabel: marketMonthlyAreaLabel(m),
      expectedLabel: formatMarketMonthly(mStats.median, block.currency),
      rangeLabel: formatMarketMonthlyRange(mStats.p25, mStats.p75, block.currency),
      // Tarjeta angosta: sin «/mes» en el rango (lo lleva «Tu unidad»).
      rangeShortLabel: formatMarketMonthlyRange(mStats.p25, mStats.p75, block.currency, { withPeriod: false }),
      unitLabel: formatMarketMonthly(m.unitTotal, block.currency),
    };
  }
  return {
    monthly: false,
    areaLabel: '',
    expectedLabel: formatMarketPerM2(basisStats?.median, block.currency, block.operation),
    rangeLabel: formatMarketRange(basisStats?.p25, basisStats?.p75, block.currency, block.operation),
    rangeShortLabel: '',
    unitLabel: formatMarketPerM2(block.unitValuePerM2, block.currency, block.operation),
  };
}

/** Lote catastral, parcela CAD o unidad tipo lote: se habla de «terrenos». */
function isMarketLand(unit) {
  return unit?.terrioType === 'lote' || unit?.itemKind === 'cadastral_lot' || unit?.itemKind === 'cad_parcel';
}

/**
 * Textos del estado «pocos comparables» (`sin_datos`): título y subtexto, sin chip. Nunca se
 * menciona el mínimo de 5 (regla interna; el usuario no puede hacer nada con eso).
 * - 0: «No encontramos comparables cerca» / «Buscamos en 1,6 km» (sin radio, solo el título;
 *   también sin bloque de mercado).
 * - N: «Todavía no hay suficientes comparables cerca» / «Encontramos 3 en 1,6 km».
 * - `n` desconocido: «Encontramos pocos en 1,6 km».
 * Lotes y CAD: «terrenos». `text` = subtexto (lo usa el aviso del Resumen, con `title`).
 */
export function describeNoData(n, radiusM, unit) {
  const noun = isMarketLand(unit) ? 'terrenos' : 'comparables';
  const radius = radiusM ? formatMarketRadius(radiusM) : '';
  const count = Number.isFinite(n) ? n : null;
  if (count === 0) {
    return { title: `No encontramos ${noun} cerca`, text: radius ? `Buscamos en ${radius}` : '' };
  }
  const found = count === null ? 'Encontramos pocos' : `Encontramos ${count}`;
  return {
    title: `Todavía no hay suficientes ${noun} cerca`,
    text: `${found}${radius ? ` en ${radius}` : ''}`,
  };
}

/**
 * Textos de la muestra baja (1 a 4 comparables, `low_sample`): título, subtexto, link y el
 * aviso único del modal (que explica que no alcanza para comparar). Lotes y CAD: «terrenos».
 */
export function describeLowSample(n, radiusM, unit) {
  const land = isMarketLand(unit);
  const one = n === 1;
  const radius = radiusM ? ` en ${formatMarketRadius(radiusM)}` : '';
  const word = land ? 'terreno' : 'comparable';
  return {
    title: one ? `Un solo ${word} cerca` : `Pocos ${word}s cerca`,
    text: `Encontramos ${n}${radius}`,
    actionLabel: one ? `Ver el ${word} ›` : `Ver los ${n} ›`,
    noticeText: one
      ? 'Es un solo caso: sirve de referencia, no para comparar el precio.'
      : 'Son pocos casos: sirven de referencia, no para comparar el precio.',
    count: n,
  };
}

/**
 * Qué muestra la tarjeta «Precio vs. mercado».
 *
 * kind:
 * - `verdict`: chip, diferencia, barra y pie («28 parecidas · confianza media»).
 * - `dubious`: superficie o precio dudoso (vacío mini con «!» + «Editar unidad»).
 * - `low_sample`: 1 a 4 comparables (`low_sample`): vacío mini + «Ver los N ›» (modal en Comparables).
 * - `no_data`: menos de 5 comparables (`title` + `text`, sin chip; ver `describeNoData`).
 * - `missing`: falta un dato de la unidad (lápiz) u otro motivo sin veredicto («!»).
 *   `dubious` y `missing` traen `title`, `text` e `icon` ('edit' | 'alert'), sin chip.
 */
export function describeMarketCard(result, forcedBlock = null) {
  const block = forcedBlock || pickMarketBlock(result);
  const unit = result?.unit || null;
  if (!block) {
    return { kind: 'no_data', ...describeNoData(0, null, unit), canEdit: false, hasReport: false };
  }
  // sin_operacion sin operación pedida: vienen venta y alquiler, ninguno con veredicto.
  const reason = block.reason || (!block.verdict && result.blocks.length > 1 ? 'sin_operacion' : null);
  if (block.verdict && !reason) {
    const basisStats = block.basis === 'similares' && block.marketSimilar ? block.marketSimilar : block.market;
    const n = basisStats?.n ?? block.nComparables ?? 0;
    const conf = block.confidence ? `confianza ${MARKET_CONFIDENCE_LABELS[block.confidence]}` : '';
    const countText = block.basis === 'similares'
      ? `${n} ${n === 1 ? 'parecida' : 'parecidas'}`
      : `${n} en la zona`;
    return {
      kind: 'verdict',
      verdict: block.verdict,
      chip: MARKET_VERDICT_LABELS[block.verdict],
      diffText: describeMarketDiff(block.diffPct),
      footer: [countText, conf].filter(Boolean).join(' · '),
      bar: computeMarketBar({
        p25: basisStats?.p25, p75: basisStats?.p75, median: basisStats?.median, value: block.unitValuePerM2,
      }),
      // Los dos extremos con la misma unidad, en un solo texto (no en los bordes de la barra,
      // que tienen aire y no son P25 / P75).
      ...describeCardLabels(block, basisStats),
      basisNote: block.basis === 'general' ? (block.basisReasonLabel || null) : null,
      operation: block.operation,
      hasReport: true,
    };
  }
  if (reason === 'sin_datos' && block.lowSample && block.comparables.length) {
    return {
      kind: 'low_sample',
      ...describeLowSample(block.comparables.length, block.search?.radiusM, unit),
      hasReport: true,
      canEdit: false,
    };
  }
  if (reason === 'sin_datos') {
    return {
      kind: 'no_data',
      ...describeNoData(block.nComparables, block.search?.radiusM, unit),
      hasReport: false,
      canEdit: false,
    };
  }
  const copyFn = REASON_COPY[reason];
  const copy = copyFn ? copyFn(unit, block) : { title: UNKNOWN_REASON_TITLE, text: block.reasonLabel || '' };
  return {
    kind: DUBIOUS_REASONS.includes(reason) ? 'dubious' : 'missing',
    reason,
    // Vacío mini sin chip (como «pocos comparables»): lápiz si falta un dato, «!» si no.
    icon: MISSING_FIELD_REASONS.includes(reason) ? 'edit' : 'alert',
    title: copy.title,
    text: copy.text,
    canEdit: EDITABLE_REASONS.includes(reason),
    // Sin veredicto igual hay informe si hay mercado (mediana, dormitorios, evolución).
    hasReport: result.blocks.some((b) => Boolean(b.market)),
  };
}

/**
 * «30–120 m², 0 a 2 dormitorios» a partir de `similarCriteria` ('' si no hay).
 */
export function describeSimilarCriteria(criteria) {
  if (!criteria) return '';
  const parts = [];
  const { surfaceMinM2: smin, surfaceMaxM2: smax, bedroomsMin: bmin, bedroomsMax: bmax } = criteria;
  if (smin !== null && smax !== null) parts.push(`${formatMarketNumber(smin)}–${formatMarketNumber(smax)} m²`);
  if (bmin !== null && bmax !== null) {
    parts.push(bmin === bmax
      ? `${bmin} ${bmin === 1 ? 'dormitorio' : 'dormitorios'}`
      : `${bmin} a ${bmax} dormitorios`);
  }
  return parts.join(', ');
}

/** `800` → `"800 m"`; `1500` → `"1,5 km"`. */
export function formatMarketRadius(meters) {
  const m = num(meters);
  if (m === null) return '';
  if (m >= 1000) return `${nf(1).format(m / 1000)} km`;
  return `${Math.round(m)} m`;
}

/**
 * Revisión de un registro para la caché: cambia cuando se edita algo que mueve la
 * comparación (precio, moneda, operación, tipo, superficie o fecha de edición).
 */
export function marketRevisionOf(record) {
  const r = obj(record);
  if (!r) return '';
  const props = obj(r.properties) || {};
  const pick = (k) => {
    const v = r[k] ?? props[k];
    return v === null || v === undefined ? '' : String(v);
  };
  return ['price', 'currency', 'operation', 'building_type', 'property_type', 'surface', 'covered_surface',
    'total_surface', 'updated_at'].map(pick).join('|');
}

/** Diferencia con signo: `-2.4` → «−2,4 %»; `18` → «+18 %». */
export function formatMarketSignedPct(diffPct) {
  const d = num(diffPct);
  if (d === null) return '';
  const abs = nf(1).format(Math.round(Math.abs(d) * 10) / 10);
  if (Math.abs(d) < 0.05) return '0 %';
  return `${d < 0 ? '−' : '+'}${abs} %`;
}

/**
 * Línea de búsqueda del informe (valores finales de la API).
 * Ampliada al barrio: «Zona ampliada al barrio Nueva Córdoba · 3 km · 36 meses».
 * Radio ampliado: «Búsqueda ampliada a 1,5 km · 36 meses». Si no: «800 m · últimos 24 meses».
 */
export function describeMarketSearch(search) {
  if (!search) return '';
  const radius = formatMarketRadius(search.radiusM);
  const months = search.months ? `${search.months} ${search.months === 1 ? 'mes' : 'meses'}` : '';
  if (search.scope === 'radio_o_barrio') {
    return [search.scopeLabel || 'Zona ampliada al barrio', radius, months].filter(Boolean).join(' · ');
  }
  if (search.widened) {
    return [radius ? `Búsqueda ampliada a ${radius}` : 'Búsqueda ampliada', months].filter(Boolean).join(' · ');
  }
  return [radius, months ? `últimos ${months}` : ''].filter(Boolean).join(' · ');
}

/** Título del aviso único del informe según el motivo. */
export function marketNoticeTitle(card) {
  if (!card) return '';
  if (card.title) return card.title;
  return card.kind === 'no_data' ? 'Todavía no hay suficientes comparables cerca' : UNKNOWN_REASON_TITLE;
}

/** Fila de «Por dormitorios» que corresponde a la unidad (4 o más → «4+»). */
export function unitBedroomsBucket(bedrooms) {
  const b = num(bedrooms);
  if (b === null || b < 0) return null;
  return Math.min(4, Math.trunc(b));
}
